/**
 * useProductSearch — the remote source of the ProductPicker (design/ux/02 §4.7 « Product picker
 * behaviour », 04 §3.2; refonte WP-22). The matching and the ranking belong to the server's one
 * search engine (WP-13, A-11 / A-43: token-AND in any order, accent- and case-insensitive, EXACT
 * barcode / N° ENR first, then prefix, substring, close spelling); this hook only asks it and maps
 * the rows. The Combobox owns the debounce, the request sequence (a late answer for an older query
 * is dropped, AUD-SRCH-06 / AUD-ACHNEW-10) and the queued Entrée.
 *
 * Sources
 *   · « Votre catalogue » — `GET /produits?search=…` with the Type d'achat as a RANKING HINT
 *     (`type_prefere`), never a filter (WP-24, A-11, AUD-SRCH-12 / AUD-PRODNEW-09): products of
 *     other types are listed with their type tag — picking one opens D2 (ux/02 §4.11) instead of
 *     the product being hidden and created twice;
 *   · « Nomenclature nationale — à créer » — Médicament and Mixte achats only, rows already in the
 *     catalogue (same N° ENR) left out; best effort: its failure never hides the catalogue rows.
 */
import * as React from "react";
import { nomenclatureApi, produitApi } from "@/services/index.js";
import { PRODUIT_PICKER_LIMITE, TYPE_ACHAT_MIXTE, libelleType } from "@/config/achat-saisie.config.js";
import { PICKER_S1 } from "@/config/achat.messages.js";
import { libelleProduit, normaliserProduit } from "@/lib/achat-saisie.js";
import { rechercherCatalogue, rechercherPos } from "@/lib/recherche-produit.js";
import { RECHERCHE_CATALOGUE_LIMITE, RECHERCHE_POS_LIMITE } from "@/config/recherche-produit.config.js";

/** Rows asked from the nomenclature next to the catalogue ones. */
const NOMENCLATURE_LIMITE = 8;

/** The Type d'achat values whose search also lists the national nomenclature (ux/02 §4.7). */
const AVEC_NOMENCLATURE = Object.freeze(["MEDICAMENT", TYPE_ACHAT_MIXTE]);

/** Query params of the catalogue read (pure; tested). */
export function parametresRecherche(q, typeAchat) {
  return {
    search: String(q ?? "").trim(),
    limit: PRODUIT_PICKER_LIMITE,
    page: 1,
    ...(typeAchat && typeAchat !== TYPE_ACHAT_MIXTE ? { type_prefere: typeAchat } : {}),
  };
}

const rowsDe = (res) => (Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);

/** Catalogue rows → options (pure; tested). */
export function optionsCatalogue(res, { typeAchat, groupe = false } = {}) {
  return rowsDe(res).map((row) => {
    const p = normaliserProduit(row);
    const autreType = p.type_produit && typeAchat && typeAchat !== TYPE_ACHAT_MIXTE && p.type_produit !== typeAchat;
    return {
      value: p.id,
      label: libelleProduit(p),
      meta: [p.dci, p.laboratoire, p.num_enr_cnas && PICKER_S1.numeroEnr(p.num_enr_cnas)].filter(Boolean).join(" · ") || undefined,
      aside: [autreType ? libelleType(p.type_produit) : null, p.stock != null ? PICKER_S1.stock(p.stock) : null]
        .filter(Boolean)
        .join(" · ") || undefined,
      group: groupe ? PICKER_S1.groupeCatalogue : undefined,
      source: "catalogue",
      produit: p,
    };
  });
}

/** Nomenclature rows → options, the ones already in the catalogue (same N° ENR) left out (pure; tested). */
export function optionsNomenclature(res, numerosCatalogue = new Set()) {
  return rowsDe(res)
    .filter((n) => !n.num_enregistrement || !numerosCatalogue.has(n.num_enregistrement))
    .map((n) => ({
      value: `nomenclature:${n.id}`,
      label: libelleProduit({ nom_commercial: n.nom_marque, dosage: n.dosage, forme: n.forme, conditionnement: n.conditionnement }),
      meta: [n.dci, n.laboratoire, n.num_enregistrement && PICKER_S1.numeroEnr(n.num_enregistrement)].filter(Boolean).join(" · ") || undefined,
      group: PICKER_S1.groupeNomenclature,
      source: "nomenclature",
      nomenclature: n,
    }));
}

/**
 * @param {string} typeAchat the facture's Type d'achat
 * @returns {(q: string, ctx: { signal?: AbortSignal }) => Promise<object[]>} the Combobox `loadOptions`
 */
export function useProductSearch(typeAchat) {
  return React.useCallback(
    async (q, { signal } = {}) => {
      const avecNomenclature = AVEC_NOMENCLATURE.includes(typeAchat);
      const [catalogue, nomenclature] = await Promise.all([
        produitApi.list(parametresRecherche(q, typeAchat), { signal }),
        avecNomenclature
          ? nomenclatureApi.search(String(q).trim(), { limit: NOMENCLATURE_LIMITE, statut_filter: "ACTIF", signal }).catch((e) => {
              if (signal?.aborted) throw e;
              return { data: [] };
            })
          : Promise.resolve({ data: [] }),
      ]);
      const cat = optionsCatalogue(catalogue, { typeAchat, groupe: avecNomenclature });
      const nums = new Set(cat.map((o) => o.produit.num_enr_cnas).filter(Boolean));
      const nom = optionsNomenclature(nomenclature, nums);
      if (nom.length === 0) return cat.map((o) => ({ ...o, group: undefined }));
      return [...cat, ...nom];
    },
    [typeAchat],
  );
}

/**
 * Counter source (XWP-12 / WP-37 — Caisse libre, Vente CHIFA, Instance CHIFA, the échange and retour
 * pickers): `GET /produits/search`, whose rows carry the sellable figure of the availability layer
 * (XWP-03 `disponibiliteCaisse`: `qte_totale`, `disponibilite`, sellable `stock_lots[]`) and the
 * match explanation (`pertinence`). Offline, the same path is answered by the local store on the
 * shared matcher (offline/offlineGate.js).
 *
 * @param {{ limit?: number, demande?: boolean, typePrefere?: string }} [opts]
 *   demande — the screen raises demands: rupture rows are pickable (the pick reports THAT product)
 */
export function usePosProductSearch({ limit = RECHERCHE_POS_LIMITE, demande = false, typePrefere } = {}) {
  return React.useCallback(
    (q, { signal } = {}) => rechercherPos(produitApi.posSearch, q, { signal, limit, demande, typePrefere }),
    [limit, demande, typePrefere],
  );
}

/**
 * Catalogue source outside the counters and the achat entry (achat urgent, appel d'offres, the
 * manquants): `GET /produits?search=` — every product of the role's catalogue, ranked by the engine.
 */
export function useCatalogueProductSearch({ limit = RECHERCHE_CATALOGUE_LIMITE } = {}) {
  return React.useCallback(
    (q, { signal } = {}) => rechercherCatalogue(produitApi.search, q, { signal, limit }),
    [limit],
  );
}

export default useProductSearch;
